import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faRotate, faStop, faDatabase, faTrash } from '@fortawesome/free-solid-svg-icons';
import {
  fetchTrialSets,
  fetchRuns,
  startRun,
  materializeBaseline,
  cancelRun,
  deleteRun,
  fetchRunSummary,
  type TrialSetSummary,
  type RunView,
  type RunStatus,
  type RunSummary,
} from '../api/skillTrials';

const V3_PROMPT = 'skill_surface_extraction.v3.prompt';
type Toast = { kind: 'info' | 'error'; text: string };

function errText(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback;
}

/**
 * Trial Runs (trial-harness spec §10.6): start the v3 and v4 arms over a set and
 * watch them run. Each arm sends every criterion in the set through its prompts and
 * records trial observations — it never touches production data.
 */
export default function TrialRuns() {
  const { t } = useTranslation();
  const [sets, setSets] = useState<TrialSetSummary[]>([]);
  const [setId, setSetId] = useState('');
  const [runs, setRuns] = useState<RunView[]>([]);
  const [summaries, setSummaries] = useState<Record<string, RunSummary>>({});
  const [starting, setStarting] = useState<'v3' | 'v4' | null>(null);
  const [materializing, setMaterializing] = useState(false);
  const [cancelling, setCancelling] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState<Set<string>>(new Set());
  const [toast, setToast] = useState<Toast | null>(null);

  const flash = useCallback((n: Toast) => setToast(n), []);
  useEffect(() => {
    if (!toast) return;
    const id = window.setTimeout(() => setToast(null), 3200);
    return () => window.clearTimeout(id);
  }, [toast]);

  useEffect(() => {
    fetchTrialSets()
      .then((l) => {
        setSets(l);
        if (l[0]) setSetId(l[0].id);
      })
      .catch((e) => flash({ kind: 'error', text: errText(e, t('skillTrials.runs.loadError')) }));
  }, [flash, t]);

  const loadRuns = useCallback(
    async (id: string) => {
      if (!id) return;
      try {
        setRuns(await fetchRuns(id));
      } catch (e) {
        flash({ kind: 'error', text: errText(e, t('skillTrials.runs.loadError')) });
      }
    },
    [flash, t]
  );

  useEffect(() => {
    void loadRuns(setId);
  }, [setId, loadRuns]);

  // Pull a summary for any COMPLETE run we don't have one for yet.
  useEffect(() => {
    runs
      .filter((r) => r.status === 'COMPLETE' && !summaries[r.id])
      .forEach((r) => {
        fetchRunSummary(r.id)
          .then((s) => setSummaries((prev) => ({ ...prev, [r.id]: s })))
          .catch(() => undefined);
      });
  }, [runs, summaries]);

  // Poll while anything is still running.
  const anyRunning = useMemo(() => runs.some((r) => r.status === 'RUNNING'), [runs]);
  useEffect(() => {
    if (!anyRunning || !setId) return;
    const id = window.setInterval(() => void loadRuns(setId), 4000);
    return () => window.clearInterval(id);
  }, [anyRunning, setId, loadRuns]);

  const start = useCallback(
    async (arm: 'v3' | 'v4') => {
      if (!setId) return;
      setStarting(arm);
      const stamp = new Date().toLocaleTimeString();
      try {
        await startRun(
          arm === 'v4'
            ? { label: `v4-five-pass · ${stamp}`, criterion_set_id: setId }
            : {
                label: `v3-baseline · ${stamp}`,
                criterion_set_id: setId,
                pass_set: [{ pass: null, prompt_file: V3_PROMPT, model: null }],
              }
        );
        flash({ kind: 'info', text: t('skillTrials.runs.started', { arm: arm.toUpperCase() }) });
        await loadRuns(setId);
      } catch (e) {
        flash({ kind: 'error', text: errText(e, t('skillTrials.runs.startError')) });
      } finally {
        setStarting(null);
      }
    },
    [setId, flash, t, loadRuns]
  );

  const baseline = useCallback(async () => {
    if (!setId) return;
    setMaterializing(true);
    try {
      await materializeBaseline({ set_id: setId });
      flash({ kind: 'info', text: t('skillTrials.runs.baselineDone') });
      await loadRuns(setId);
    } catch (e) {
      flash({ kind: 'error', text: errText(e, t('skillTrials.runs.baselineError')) });
    } finally {
      setMaterializing(false);
    }
  }, [setId, flash, t, loadRuns]);

  const cancel = useCallback(
    async (runId: string) => {
      setCancelling((prev) => new Set(prev).add(runId));
      try {
        await cancelRun(runId);
        flash({ kind: 'info', text: t('skillTrials.runs.cancelRequested') });
        await loadRuns(setId);
      } catch (e) {
        flash({ kind: 'error', text: errText(e, t('skillTrials.runs.cancelError')) });
        setCancelling((prev) => {
          const next = new Set(prev);
          next.delete(runId);
          return next;
        });
      }
    },
    [setId, flash, t, loadRuns]
  );

  const del = useCallback(
    async (runId: string) => {
      if (!window.confirm(t('skillTrials.runs.deleteConfirm'))) return;
      setDeleting((prev) => new Set(prev).add(runId));
      try {
        await deleteRun(runId);
        await loadRuns(setId);
      } catch (e) {
        flash({ kind: 'error', text: errText(e, t('skillTrials.runs.deleteError')) });
        setDeleting((prev) => {
          const next = new Set(prev);
          next.delete(runId);
          return next;
        });
      }
    },
    [setId, flash, t, loadRuns]
  );

  const completeCount = runs.filter((r) => r.status === 'COMPLETE').length;

  return (
    <div className="mx-auto max-w-[820px] px-4 py-6" style={{ color: '#0f172a' }}>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">{t('skillTrials.runs.title')}</h1>
          <p className="mt-0.5 text-sm text-slate-500">{t('skillTrials.runs.subtitle')}</p>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <span className="text-slate-500">{t('skillTrials.runs.set')}</span>
          <select
            className="rounded-lg border px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
            style={{ borderColor: '#cbd5e1' }}
            value={setId}
            onChange={(e) => setSetId(e.target.value)}
            disabled={sets.length === 0}
          >
            {sets.length === 0 && <option value="">—</option>}
            {sets.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* Start buttons */}
      <div className="mb-3 flex flex-wrap items-center gap-3 rounded-[14px] border bg-white p-4 shadow-sm" style={{ borderColor: '#e2e8f0' }}>
        <button
          disabled={!setId || starting !== null}
          onClick={() => start('v4')}
          className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <FontAwesomeIcon icon={faPlay} className="text-xs" />
          {starting === 'v4' ? t('skillTrials.runs.starting') : t('skillTrials.runs.startV4')}
        </button>
        <button
          disabled={!setId || materializing}
          onClick={baseline}
          title={t('skillTrials.runs.baselineHint')}
          className="inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          style={{ borderColor: '#cbd5e1' }}
        >
          <FontAwesomeIcon icon={faDatabase} className="text-xs" />
          {materializing ? t('skillTrials.runs.materializing') : t('skillTrials.runs.baseline')}
        </button>
        <button
          disabled={!setId || starting !== null}
          onClick={() => start('v3')}
          title={t('skillTrials.runs.startV3Hint')}
          className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-[13px] text-slate-500 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          style={{ borderColor: '#e2e8f0' }}
        >
          <FontAwesomeIcon icon={faPlay} className="text-[10px]" />
          {starting === 'v3' ? t('skillTrials.runs.starting') : t('skillTrials.runs.startV3')}
        </button>
        <button
          onClick={() => loadRuns(setId)}
          className="ml-auto inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-600"
          title={t('common.refresh')}
        >
          <FontAwesomeIcon icon={faRotate} className={anyRunning ? 'animate-spin text-xs' : 'text-xs'} />
          {t('common.refresh')}
        </button>
      </div>
      <p className="mb-4 px-1 text-[11.5px] text-slate-400">{t('skillTrials.runs.hint')}</p>

      {/* Runs list */}
      {runs.length === 0 ? (
        <div className="rounded-[14px] border bg-white py-12 text-center text-sm text-slate-500 shadow-sm" style={{ borderColor: '#e2e8f0' }}>
          {setId ? t('skillTrials.runs.none') : t('skillTrials.runs.pickSet')}
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {runs.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center gap-3 rounded-xl border bg-white px-4 py-3 shadow-sm" style={{ borderColor: '#e2e8f0' }}>
              <StatusBadge status={r.status} />
              <div className="min-w-0 flex-1">
                <div className="truncate font-mono text-[13px] font-medium">{r.label}</div>
                <div className="text-[11px] text-slate-400">
                  {new Date(r.started_at).toLocaleString()}
                  {summaries[r.id] && (
                    <>
                      {' · '}
                      {t('skillTrials.runs.observations', { n: summaries[r.id].observations })}
                      {' · '}
                      {t('skillTrials.runs.wouldOrphan', { n: summaries[r.id].would_orphan })}
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                {r.status === 'RUNNING' && (
                  <button
                    onClick={() => cancel(r.id)}
                    disabled={cancelling.has(r.id)}
                    className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[12px] text-red-600 hover:bg-red-50 disabled:opacity-50"
                    style={{ borderColor: '#e6b8b8' }}
                  >
                    <FontAwesomeIcon icon={faStop} className="text-[10px]" />
                    {cancelling.has(r.id) ? t('skillTrials.runs.cancelling') : t('skillTrials.runs.cancel')}
                  </button>
                )}
                <button
                  onClick={() => del(r.id)}
                  disabled={deleting.has(r.id)}
                  title={t('skillTrials.runs.delete')}
                  aria-label={t('skillTrials.runs.delete')}
                  className="inline-flex items-center rounded-md px-2 py-1 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                >
                  <FontAwesomeIcon icon={faTrash} className="text-[11px]" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {completeCount >= 2 && (
        <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-800">
          {t('skillTrials.runs.readyToAdjudicate')}
        </div>
      )}

      {toast && (
        <div role="alert" className={`fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-lg px-4 py-2 text-sm text-white shadow-lg ${toast.kind === 'error' ? 'bg-red-600' : 'bg-slate-800'}`}>
          {toast.text}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: RunStatus }) {
  const { t } = useTranslation();
  const styles: Record<RunStatus, string> = {
    RUNNING: 'bg-amber-100 text-amber-700',
    COMPLETE: 'bg-emerald-100 text-emerald-700',
    FAILED: 'bg-red-100 text-red-700',
    CANCELLED: 'bg-slate-200 text-slate-600',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${styles[status]}`}>
      {status === 'RUNNING' && <FontAwesomeIcon icon={faRotate} className="animate-spin text-[9px]" />}
      {t(`skillTrials.runs.status.${status}`)}
    </span>
  );
}
